import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { BuildingConfig } from '@cobu/types';

const shouldShowGroups = (
    buildingConfig: BuildingConfig | null | undefined
): boolean => {
    return buildingConfig?.showGroups ?? true;
};


const shouldShowDM = (
    buildingConfig: BuildingConfig | null | undefined
): boolean => {
    return buildingConfig?.showDM ?? true;
};


const shouldShowEvents = (
    buildingConfig: BuildingConfig | null | undefined
): boolean => {
    return !(buildingConfig?.hideEvents ?? false);
};

// Unlike the other flags, Places is opt-in per building — the tab and the
// /places routes only exist when the config says so.
const shouldShowPlaces = (
    buildingConfig: BuildingConfig | null | undefined
): boolean => {
    return buildingConfig?.showPlaces ?? false;
};

const shouldAllowResidentEventCreation = (
    buildingConfig: BuildingConfig | null | undefined
): boolean => {
    if (buildingConfig?.hideEvents) return false;
    return buildingConfig?.residentsCanCreateEvents ?? true;
};

// Pure form of the hook, for callers that hold a config the store hasn't
// applied yet — during session resolve, the dispatch and the code that needs
// the flags run in the same tick, so the hook's value is still a render behind.
export const deriveFeatureFlags = (
    buildingConfig: BuildingConfig | null | undefined
) => ({
    showGroups: shouldShowGroups(buildingConfig),
    showDM: shouldShowDM(buildingConfig),
    showEvents: shouldShowEvents(buildingConfig),
    showPlaces: shouldShowPlaces(buildingConfig),
    residentsCanCreateEvents: shouldAllowResidentEventCreation(buildingConfig),
});

export const useFeatureFlags = () => {
    const buildingConfig = useSelector((state: RootState) => state.settings.buildingConfig);

    return useMemo(() => deriveFeatureFlags(buildingConfig), [buildingConfig]);
};
